/**
 * API connexions plateformes (S6 PR1) — wrapping autour de `apiClient`.
 *
 * Source de vérité : routes `/api/auth/etsy/*` + `/api/platform-connections`.
 * Pas de mapper BE↔FE — types reflètent strictement le codegen OpenAPI.
 */

import { apiClient } from './client'
import type {
  EtsyOAuthStartResponse,
  PinterestBoardResponse,
  PinterestCancelBlockedResponse,
  PinterestMarkStandardResponse,
  PinterestOAuthStartResponse,
  PinterestPublishRequest,
  PinterestPublishResponse,
  PinterestQuotaState,
  Platform,
  PlatformConnectionResponse,
} from '@/types/platform-connections'

export const platformConnectionsApi = {
  list: async (platform?: Platform): Promise<PlatformConnectionResponse[]> => {
    const params = platform ? { platform } : undefined
    const { data } = await apiClient.get<PlatformConnectionResponse[]>('/platform-connections', {
      params,
    })
    return data
  },

  disconnect: async (connectionId: number): Promise<void> => {
    await apiClient.delete(`/platform-connections/${connectionId}`)
  },

  startEtsyOAuth: async (): Promise<EtsyOAuthStartResponse> => {
    const { data } = await apiClient.get<EtsyOAuthStartResponse>('/auth/etsy/start')
    return data
  },

  startPinterestOAuth: async (): Promise<PinterestOAuthStartResponse> => {
    const { data } = await apiClient.get<PinterestOAuthStartResponse>('/auth/pinterest/start')
    return data
  },

  listPinterestBoards: async (connectionId: number): Promise<PinterestBoardResponse[]> => {
    const { data } = await apiClient.get<PinterestBoardResponse[]>(
      `/platform-connections/${connectionId}/pinterest-boards`,
    )
    return data
  },

  refreshPinterestBoards: async (connectionId: number): Promise<PinterestBoardResponse[]> => {
    const { data } = await apiClient.post<PinterestBoardResponse[]>(
      `/platform-connections/${connectionId}/pinterest-boards/refresh`,
    )
    return data
  },

  // S7-PR2 — Publish Pinterest (board picker + strategy carousel/separate).
  publishPinterest: async (
    workId: number,
    payload: PinterestPublishRequest,
  ): Promise<PinterestPublishResponse> => {
    const { data } = await apiClient.post<PinterestPublishResponse>(
      `/works/${workId}/publications/pinterest`,
      payload,
    )
    return data
  },

  retryPinterestBlockedMono: async (
    workId: number,
    resourceId: number,
  ): Promise<PinterestPublishResponse> => {
    const { data } = await apiClient.post<PinterestPublishResponse>(
      `/works/${workId}/publications/pinterest/${resourceId}/retry-mono`,
    )
    return data
  },

  cancelPinterestBlocked: async (
    workId: number,
    resourceId: number,
  ): Promise<PinterestCancelBlockedResponse> => {
    const { data } = await apiClient.post<PinterestCancelBlockedResponse>(
      `/works/${workId}/publications/pinterest/${resourceId}/cancel-blocked`,
    )
    return data
  },

  // Quota state — surfacé en Settings UI (badge "Quota du jour").
  getPinterestQuotaState: async (): Promise<PinterestQuotaState> => {
    const { data } = await apiClient.get<PinterestQuotaState>('/pinterest/quota')
    return data
  },

  // Admin — flip pinterest.is_standard=True après accord Pinterest.
  markPinterestConnectionStandard: async (
    connectionId: number,
  ): Promise<PinterestMarkStandardResponse> => {
    const { data } = await apiClient.post<PinterestMarkStandardResponse>(
      `/admin/platform-connections/${connectionId}/mark-standard`,
    )
    return data
  },
}
